import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';

/**
 * Dropdown estilo Medicus para reemplazar el `<select>` nativo del browser
 * en los formularios (ver image24 del QA del 16/05).
 *
 * Diferencias respecto al `<select>`:
 *  - Diseño consistente cross-browser (el nativo se ve distinto en cada OS)
 *  - Opciones con padding cómodo + check del seleccionado
 *  - Cierra al hacer click afuera o con Escape
 *  - Mismo border-radius y padding que `.input` para encajar con el resto
 *    del form
 */
export interface DropdownOption<V extends string = string> {
  value: V;
  label: string;
}

export function MedicusDropdown<V extends string = string>({
  value,
  onChange,
  options,
  placeholder = '— seleccioná —',
  disabled,
  id,
}: {
  value: V | '';
  onChange: (v: V) => void;
  options: ReadonlyArray<DropdownOption<V>>;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onDoc);
      document.addEventListener('keydown', onKey);
    }
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        id={id}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="input"
        style={{
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          color: selected ? 'var(--color-element-high)' : 'var(--color-element-low)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          background: 'var(--color-background)',
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown
          size={16}
          style={{
            transition: 'transform 0.15s',
            transform: open ? 'rotate(180deg)' : undefined,
            flexShrink: 0,
          }}
        />
      </button>
      {open && (
        <ul
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            background: 'var(--color-background)',
            border: '1px solid var(--color-outline)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg)',
            zIndex: 20,
            maxHeight: 260,
            overflowY: 'auto',
            padding: 4,
            listStyle: 'none',
            margin: 0,
          }}
        >
          {options.map((o) => {
            const isSelected = o.value === value;
            return (
              <li key={o.value}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                  role="option"
                  aria-selected={isSelected}
                  style={{
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    padding: '10px 12px',
                    borderRadius: 'var(--radius-sm)',
                    background: isSelected ? 'var(--color-medicus-celeste-bg)' : 'transparent',
                    color: 'var(--color-element-high)',
                    fontSize: 14,
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected)
                      (e.currentTarget as HTMLElement).style.background =
                        'var(--color-primary-darker-5)';
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) (e.currentTarget as HTMLElement).style.background = 'transparent';
                  }}
                >
                  <span>{o.label}</span>
                  {isSelected && (
                    <Check size={14} strokeWidth={3} color="var(--color-primary-lighter)" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
